import React, { useContext, useState } from 'react'
import { OptionContent } from '../style'
import { ClimaOptionsContext } from '..'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import { useTranslation } from 'react-i18next'
import { Popup, Table } from 'semantic-ui-react'
import InfoPopup from '~/components/InfoPopup'
import { formatNumber } from '~/utils/helpers'
import CustomIcon from '~/components/CustomIcon'
import { OrganicFertilizerType } from '~/api/services/climaCheck'
import EditFertilizers from './EditFertilizers'
import Total from './Total'

export const fertilizerDataFields = [
  {
    name: 'TS',
    key: 'dryMatter',
    unit: '% TM'
  },
  {
    name: 'Organische Substanz',
    key: 'organicMatter',
    unit: '%'
  },
  {
    name: 'N',
    key: 'n',
    unit: 'kg/t bzw. kg/m³'
  },
  {
    name: '<span>NH<sub>4</sub>-N (N<sub>verfügbar</sub>)</span>',
    key: 'nh4N',
    unit: 'kg/t bzw. kg/m³'
  },
  {
    name: 'N-Aufbringungsverluste',
    key: 'nLosses',
    unit: '%'
  },
  {
    name: 'P2O5',
    key: 'p2o5',
    unit: 'kg/t bzw. kg/m³'
  },
  {
    name: '<span>K<sub>2</sub>O</span>',
    key: 'k2o',
    unit: 'kg/t bzw. kg/m³'
  }
]

const SecondStep:React.FC = () => {
  const { t } = useTranslation()
  const { organicFertilizers, grassSilageOrganicFertilizerApplicationTechniques } = useContext(ClimaOptionsContext)

  const { control, getValues, clearErrors, setValue } = useFormContext()

  const { fields, replace, update } = useFieldArray({
    control,
    name: 'grassSilage.organicFertilizers',
    keyName: 'key'
  })

  const [fertilizerData, setFertilizerData] = useState<OrganicFertilizerType>(null)

  const tableColumnHeaders = [
    {
      name: 'Düngeart',
      key: 'label',
      type: 'fertilizer-fields'
    },
    {
      name: 'Ausbringmenge',
      key: 'value',
      type: 'input'
    },
    {
      name: 'Ausbringungstechnik',
      key: 'organicFertilizerApplicationTechniqueId',
      type: 'select',
      optionsKey: 'organicFertilizerApplicationTechniques'
    }
  ];

  function renderInputCellValue(name, unit: string) {
    const unitTransformed = `${unit?.toString()?.startsWith('m3') ? 'm³' : unit}/ha`
    return <FormFields.Input
      placeholder={`in ${unitTransformed}`}
      name={name}
      id={name}
      onChange={(event) => {
        setValue(name, event?.target?.value?.replace(unitTransformed, ''))
        clearErrors(name)
      }}
      control={control}
      suffix={` ${unitTransformed}`}
      typeInput={'controled'}
      withoutAsterisk
      noErrorMessage
      allowDecimals
      decimalScale={2}
    />
  }

  function renderSelect(name) {
    return <FormFields.Select
      name={name}
      style={{ width: 380 }}
      placeholder={'Art der Ausbringungstechhnik'}
      control={control}
      onChange={(e, data) => {
        setValue(name, data.value)
        clearErrors(name)
      }}
      scrolling
      withoutAsterisk
      noErrorMessage
      options={grassSilageOrganicFertilizerApplicationTechniques.map(orgFertAppTechnique => ({ value: orgFertAppTechnique.id, text: orgFertAppTechnique.label }))}
    />
  }

  function renderFertilizerFieldCell(organicFertilizer, name, column) {
    return <>
      <div>{organicFertilizer.label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <small style={{ fontSize: '85%' }}>
          {t('[TM: {{dryMatter}} % TM, Organische Substanz: {{organicMatter}} %]...',
            {
              dryMatter: formatNumber(organicFertilizer?.dryMatter?.replace(',', '.'), 2),
              organicMatter: formatNumber(organicFertilizer?.organicMatter?.replace(',', '.'), 2)
            }
          )}
        </small>
        <div style={{ display: 'flex', gap: 5, marginLeft: 7 }}>
          <InfoPopup
            position={'top center'}
            styleIcon={{ fontSize: '1.2em' }}
            content={<small style={{ fontSize: '85%' }}>
              {fertilizerDataFields.map(fertilizeField => {
                return <div
                  key={fertilizeField.key}
                  dangerouslySetInnerHTML={{ __html: `${fertilizeField.name}: ${formatNumber(organicFertilizer[fertilizeField.key]?.replace(',', '.'), 2)} ${fertilizeField.unit}` }}
                />
              })}
            </small>}
          />
          <Popup
            content={<div>
              <div>{t('Change')}</div>
            </div>}
            trigger={<CustomIcon
              name={'pencil'}
              onClick={() => setFertilizerData(organicFertilizer)}
              style={{ fontSize: '1.2em' }}
            />}
          />
        </div>
      </div>
    </>
  }

  function getTotal(organicFertilizer) {
    const value = parseFloat(organicFertilizer?.value?.replace(',', '.')) || 0
    const nitroValue = parseFloat(organicFertilizer?.n?.replace(',', '.')) || 0
    return `${formatNumber(value * nitroValue)} kg N/m3`
  }

  function renderCell(organicFertilizer, name, column) {
    switch(column.type){
      case 'input':
        return renderInputCellValue(name, organicFertilizer?.unit)
      case 'select':
        return renderSelect(name)
      case 'fertilizer-fields':
        return renderFertilizerFieldCell(organicFertilizer, name, column)
      case 'total':
        return getTotal(organicFertilizer)
      default:
        return organicFertilizer[column.key]
    }
  }

  function renderTableBody() {
    return fields.map((organicFertilizer, indexFertilizer) => {
      return <Table.Row key={organicFertilizer.key}>
        {tableColumnHeaders.map(column => {
          const name = `grassSilage.organicFertilizers[${indexFertilizer}].${column.key}`
          return <Table.Cell key={`${organicFertilizer.key}-${column.key}`}>
            {renderCell(organicFertilizer, name, column)}
          </Table.Cell>
        })}
      </Table.Row>
    })
  }

  function renderTable() {
    return <Table>
      <Table.Header>
        <Table.Row>
          {tableColumnHeaders.map(header => {
            return <Table.HeaderCell key={header.key}>
              {header.name}
            </Table.HeaderCell>
          })}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {renderTableBody()}
      </Table.Body>
    </Table>
  }

  function renderEditFertilizerData() {
    return <EditFertilizers
      fertilizerData={fertilizerData}
      handleClose={() => setFertilizerData(null)}
      handleSubmit={(fertilizerData, updateData) => {
        const index = fields.findIndex(fertilizer => fertilizer['id'] === fertilizerData.id)

        if(index >= 0) {
          update(index, {
            ...fertilizerData,
            ...updateData
          })
          setFertilizerData(null)
        }
      }}
    />
  }

  return <>
    {renderEditFertilizerData()}
    <OptionContent>
      <FormFields.Select
        label={'Welchen Wirtschaftsdünger haben Sie aufgebracht?'}
        control={control}
        id={'grassSilage.organicFertilizers'}
        placeholder='Art des Wirtschaftsdünger'
        name=''
        value={fields.map(fertilizer => fertilizer['id'])}
        options={organicFertilizers.map(type => ({ value: type.id, text: type.label }))}
        onChange={(_, { value }: {value: number[]}) => {
          const organicFertilizersValue = value.map(id => organicFertilizers.find(fertilizer => fertilizer.id === id))
          const fertilizers = getValues('grassSilage.organicFertilizers')
          const includedFertilizerValues = value.map(id => fertilizers.find(field => field['id'] === id) || organicFertilizersValue.find(n => n.id === id))
          if(includedFertilizerValues.length > 0) {
            const targetElement = document.getElementById('grassSilage.organicFertilizers')
            targetElement?.classList?.remove('error');
          }
          replace(includedFertilizerValues)
        }}
        multiple
        search
        noResultsMessage={t('No results found....')}
        noErrorMessage
        withoutAsterisk
      />
      {fields.length > 0 && renderTable()}
      {fields.length > 0 && <Total/>}
    </OptionContent>
  </>
}
export default SecondStep